import { z } from 'zod';
import { createFileRoute, redirect } from '@tanstack/react-router';

import { NewEncounterPage } from '@/features/emr/pages/NewEncounterPage';

const searchSchema = z.object({
  patientId: z.string().optional(),
});

/**
 * Read the persisted edition from the org snapshot that AuthContext writes to
 * localStorage (`selected_org`). The route loader runs before AuthContext is
 * fully mounted, so we read storage directly. Anything malformed falls through
 * to the regular Professional UI — that's safe even for Personal users; in
 * the rare case where the snapshot is missing, AuthContext's later hydration
 * will still hide the New Encounter form via the rest of the Personal shell.
 */
function readEditionFromStorage(): 'personal' | 'professional' | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem('selected_org');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { edition?: string };
    return parsed.edition === 'personal' ? 'personal' : 'professional';
  } catch {
    return null;
  }
}

export const Route = createFileRoute('/_authenticated/encounters/new')({
  component: NewEncounterPage,
  validateSearch: searchSchema,
  beforeLoad: ({ search }) => {
    if (readEditionFromStorage() === 'personal') {
      throw redirect({
        to: '/quick-start',
        search: search.patientId ? { patientId: search.patientId } : undefined,
      });
    }
    return {
      breadcrumbs: [
        { href: '/encounters', title: 'Encounters' },
        { title: 'New Encounter', href: '/encounters/new' },
      ],
    };
  },
});
